// The content images: real Photohawk Studio screenshots and diagrams drawn in code, used by the blog's articles, the
// event photography pages and the Studio landing pages. Each is served from public/images/content as AVIF, WebP and
// JPEG at several widths (<name>-<width>.<ext>), made by scripts/content-images (docs/seo-content-plan.md, "Images"). images.json, written
// by encode.py, holds the widths and intrinsic sizes; this file holds what each image shows: its default alt text,
// caption and where it came from.
//
// Use one in an article body as ":::figure <name>" (alt: and caption: there can say it better for that article), or
// as an article's heroImage. File names are descriptive kebab-case: they're part of how image search reads them.
//
// Image rules (docs/seo-content-plan.md; the owner, 20 October 2026): real product UI, but never a real person's
// identifiable face except in licensed stock photos (Pexels License: CREDITS.md). Where the UI shows photos (the
// gallery, search, the viewer, the uploader) they are stock photos drawn into the captured UI
// (scripts/content-images/stock_mocks.py), and the event photography pages' heroes are stock photos. Never personal
// data (e-mail addresses are replaced with alex@example.com; gallery names are demo names); diagrams show how things
// work, never a result we haven't measured.

import MANIFEST from "./images.json";

export type MediaKind = "screenshot" | "diagram" | "photo";

/** A stock photo's credit (Pexels License: attribution isn't required, but we give it). */
export interface MediaCredit {
  photographer: string;
  /** The photographer's Pexels profile. */
  profile: string;
  /** The photo's Pexels page. */
  page: string;
}

export interface MediaMeta {
  kind: MediaKind;
  /** The default alt text: what the image shows, for someone who can't see it. */
  alt: string;
  /** The default visible caption. */
  caption: string;
  /** Where it came from (for the image sitemap's notes and the owner): a staging page, or "diagrams.mjs". */
  source: string;
  /** For a stock photo: who took it (shown with it). */
  credit?: MediaCredit;
}

export interface MediaImage extends MediaMeta {
  name: string;
  /** Intrinsic size at the largest width (for width/height, so nothing shifts as it loads). */
  width: number;
  height: number;
  widths: number[];
  /** The JPEG at a mid width: the <img> fallback and the image sitemap's URL. */
  src: string;
  /** srcset per format. */
  srcSet: { avif: string; webp: string; jpg: string };
}

export const MEDIA_DIR = "/images/content";

export const mediaUrl = (name: string, width: number, ext: "avif" | "webp" | "jpg") => `${MEDIA_DIR}/${name}-${width}.${ext}`;

const DIAGRAM = "Drawn in code: scripts/content-images/diagrams.mjs";
const SHOTS =
  "Staging, captured read-only by photohawk-serverless's shot-studio-seo workflow (branch claude/shots-studio-seo): the demo workspace's public galleries, and the test workspace shown with demo names";
const MOCK =
  "The real gallery UI as captured on staging (shot-studio-seo), with every photo replaced by Pexels stock photos drawn the way the gallery draws them (scripts/content-images/stock_mocks.py; CREDITS.md)";
const PEXELS = "Pexels stock photo, Pexels License (CREDITS.md)";

/** What each image shows. Every entry needs files (images.json), and every file an entry: media.test.ts. */
export const MEDIA_META: Record<string, MediaMeta> = {
  // ---------- Screenshots (Photohawk Studio on staging, October 2026) ----------
  "event-gallery-photo-grid": {
    kind: "screenshot",
    alt: "A Photohawk Studio event gallery of road race photos in a grid, each with the PREVIEW watermark and its capture time, under the gallery's name and a Find your photos button.",
    caption: "An event gallery on Photohawk Studio, searchable by bib number or a selfie. Photos: Pexels stock, by Eddson Lens and RUN 4 FFWPU.",
    source: MOCK,
  },
  "bib-number-search-dialog": {
    kind: "screenshot",
    alt: "The Find your photos box in a Photohawk Studio race gallery, with bib number 1423 typed in, a button to add a selfie and a Search button, over the dimmed gallery of runners.",
    caption: "Find your photos: type a bib number, add a selfie, or both.",
    source: MOCK,
  },
  "selfie-search-results": {
    kind: "screenshot",
    alt: "A Photohawk Studio gallery after a selfie search: the two photos of the same trail runner that the selfie matched, with a Reset button to go back to every photo.",
    caption: "Selfie search results: the photos the selfie matched, for the person to check. Photos: Pexels stock, by RUN 4 FFWPU.",
    source: MOCK,
  },
  "event-photo-buy-options": {
    kind: "screenshot",
    alt: "The price list in a Photohawk Studio race gallery: downloads priced for one photo and for bundles of photos, with tabs for prints and videos. The demo prices are blurred.",
    caption: "A gallery's price list: single photos and bundles, set by the photographer.",
    source: SHOTS,
  },

  "web-uploader-live-on-previews": {
    kind: "screenshot",
    alt: "The Photohawk web uploader mid-upload: the status bar says the gallery is live on previews and shows how many people are visiting, the grid shows the race photos, and the queue shows the originals on their way.",
    caption: "Mid-upload with previews first: the gallery is live while the originals follow. Photos: Pexels stock.",
    source: "The /web-uploader launch page's screenshot of the real uploader with demo data, its thumbnails replaced by Pexels stock photos (scripts/content-images/stock_mocks.py; CREDITS.md)",
  },
  "on-demand-ftp-server-panel": {
    kind: "screenshot",
    alt: "The Your FTP server panel in the Photohawk web uploader, showing a ready server with its address, port, demo username, passive mode and FTP over TLS, and Keep it running and Stop buttons.",
    caption: "The on-demand FTP server panel: start your own FTP server when you need it, on every plan.",
    source: "The /on-demand-ftp launch page's screenshot: the real panel with demo data (src/assets/on-demand-ftp)",
  },
  "dashboard-sales-insights": {
    kind: "screenshot",
    alt: "The Photohawk Studio dashboard for the year to date: revenue, orders, average order value and new customers, with revenue over time and orders by gallery.",
    caption: "The Studio dashboard: revenue, orders and customers at a glance, and which galleries sell.",
    source: SHOTS,
  },
  "preview-protection-reveal-viewer": {
    kind: "screenshot",
    alt: "A road race photo opened in a Photohawk Studio gallery with preview protection: the runner is softly blurred below the face, and a panel beside it shows them clearly in a band you scan with a slider.",
    caption: "Preview protection: tap a person to see them clearly, while the preview itself can't stand in for the photo. Photo: RUN 4 FFWPU on Pexels.",
    source: MOCK,
  },
  "preview-protection-blurred-preview": {
    kind: "screenshot",
    alt: "A Photohawk gallery preview with the runners softly blurred below the face and their faces kept clear, so the preview can't stand in for the photo.",
    caption: "Preview protection: people are softly blurred in previews, and faces stay clear.",
    source: "The /preview-protection demo (public/images/demo/preview-protection), rendered from a stock marketing image (src/pages/PreviewProtection.tsx)",
  },

  // ---------- Stock photos (Pexels License), the event photography pages' heroes ----------
  "marathon-runners-city-road-stock": {
    kind: "photo",
    alt: "A large field of runners with race bibs on a city road during a marathon.",
    caption: "A big field on a city road: hundreds of runners, and every one of them wants their photos.",
    source: PEXELS,
    credit: { photographer: "RUN 4 FFWPU", profile: "https://www.pexels.com/@runffwpu", page: "https://www.pexels.com/photo/men-in-uniform-running-marathon-10313672/" },
  },
  "triathlon-swim-start-sunrise-stock": {
    kind: "photo",
    alt: "Triathletes running into the sea at sunrise for the swim start of a triathlon in Alicante, Spain, silhouetted against the sun.",
    caption: "Swim start at sunrise: no bibs in the water, so faces and times do the work.",
    source: PEXELS,
    credit: { photographer: "Ana Hidalgo Burgos Burgos", profile: "https://www.pexels.com/@ana-hidalgo-burgos-burgos-4848204", page: "https://www.pexels.com/photo/people-bathing-in-the-sea-at-sunrise-21365541/" },
  },
  "cycling-sportive-riders-street-stock": {
    kind: "photo",
    alt: "Cyclists in helmets and club kit racing round a corner on a town street.",
    caption: "Riders through town: on the bike, numbers are often hidden.",
    source: PEXELS,
    credit: { photographer: "Haberdoedas Photography", profile: "https://www.pexels.com/@haberdoedas", page: "https://www.pexels.com/photo/exciting-road-bicycle-race-with-competitors-32917702/" },
  },
  "trail-ultra-runners-cappadocia-stock": {
    kind: "photo",
    alt: "Trail runners with race vests and bibs on a path in Cappadocia, with hot air balloons rising in the sky behind them.",
    caption: "A trail race at sunrise: remote, long, and with photographers spread far apart.",
    source: PEXELS,
    credit: { photographer: "Emrah Yazıcıoğlu", profile: "https://www.pexels.com/@emrah-yazicioglu-275583761", page: "https://www.pexels.com/photo/group-of-men-running-steep-uphill-with-hot-air-balloons-background-12918263/" },
  },
  "show-jumping-horse-rider-stock": {
    kind: "photo",
    alt: "A rider in a black jacket and helmet on a chestnut horse clearing a show jumping fence at an outdoor competition.",
    caption: "Show jumping: riders look for their round, by class and ring.",
    source: PEXELS,
    credit: { photographer: "Genadi Yakovlev", profile: "https://www.pexels.com/@gy1610", page: "https://www.pexels.com/photo/dynamic-equestrian-show-jumping-competition-37633903/" },
  },
  "mud-run-obstacle-crawl-stock": {
    kind: "photo",
    alt: "A woman pushing through a muddy pit in an obstacle race, splashed with mud.",
    caption: "Mud runs: by the second obstacle, nobody's bib can be read.",
    source: PEXELS,
    credit: { photographer: "Fernando Lucas", profile: "https://www.pexels.com/@fernando-lucas-95845401", page: "https://www.pexels.com/photo/woman-competing-in-extreme-mud-obstacle-race-37926868/" },
  },
  "runner-celebrating-race-finish-stock": {
    kind: "photo",
    alt: "A smiling runner in a cap, sunglasses and a yellow vest spreading his arms near the end of a race, with other runners behind him.",
    caption: "The photo people want most: the moment they finish.",
    source: PEXELS,
    credit: { photographer: "Kiran More", profile: "https://www.pexels.com/@kiran-more-1081549420", page: "https://www.pexels.com/photo/joyful-marathon-runner-celebrating-finish-32944993/" },
  },
  "fun-run-start-arch-stock": {
    kind: "photo",
    alt: "Runners leaving the start line under a blue inflatable START arch on grass, with spectators watching, at the Kahala Marathon in Estonia.",
    caption: "The start line: where sponsors' arches and banners are in every photo.",
    source: PEXELS,
    credit: { photographer: "Margo Evardson", profile: "https://www.pexels.com/@margo-evardson-2158292018", page: "https://www.pexels.com/photo/runners-at-the-start-of-kahala-marathon-38656924/" },
  },
  "runner-finish-line-race-clock-stock": {
    kind: "photo",
    alt: "A runner in a white vest breaking the finish tape of a town road race, arms raised, with the race clock beside him.",
    caption: "Finish times and finish photos belong together.",
    source: PEXELS,
    credit: { photographer: "RUN 4 FFWPU", profile: "https://www.pexels.com/@runffwpu", page: "https://www.pexels.com/photo/men-s-white-and-black-tank-top-3075367/" },
  },
  "runner-with-finisher-medal-stock": {
    kind: "photo",
    alt: "A smiling runner in sunglasses holding up her finisher medal and making a peace sign after a race.",
    caption: "After the race, the next thing people look for is their photos.",
    source: PEXELS,
    credit: { photographer: "Heriberto Jahir Medina", profile: "https://www.pexels.com/@detrasdelfotografo", page: "https://www.pexels.com/photo/a-woman-holding-a-medal-after-completing-a-marathon-24964258/" },
  },
  "event-photographers-at-race-stock": {
    kind: "photo",
    alt: "Photographers with cameras working on the grass at a horse racing festival in Vietnam, in front of a crowd with umbrellas.",
    caption: "Photographers at work on the day: choose the partner who gets their photos to people fast.",
    source: PEXELS,
    credit: { photographer: "Vietnam Hidden Light", profile: "https://www.pexels.com/@vietnamhiddenlight", page: "https://www.pexels.com/photo/photographers-capturing-bac-ha-festival-moments-32752133/" },
  },
  "race-volunteer-drink-station-stock": {
    kind: "photo",
    alt: "A triathlete in a race suit and sunglasses taking a cup of water from a volunteer at a drink station during a race.",
    caption: "Organisers and volunteers make the day: and decide how participants' photos and data are handled.",
    source: PEXELS,
    credit: { photographer: "K", profile: "https://www.pexels.com/@kelly", page: "https://www.pexels.com/photo/runner-receiving-water-during-marathon-race-event-34089573/" },
  },

  // ---------- Diagrams ----------
  "previews-first-upload-to-sale-diagram": {
    kind: "diagram",
    alt: "Diagram: four steps from upload to sale. Upload in the web uploader; light previews go up first and the gallery is live; the originals follow in the background; a photo someone buys jumps the queue.",
    caption: "With previews first, the gallery is live on light previews while the originals upload in the background.",
    source: DIAGRAM,
  },
  "selfie-search-how-it-works-diagram": {
    kind: "diagram",
    alt: "Diagram: a selfie is turned into a face template, compared with the gallery's face search index, and returns possible matches. The selfie isn't stored, and face data is deleted at the latest 12 months after upload.",
    caption: "How selfie search works: the selfie is used for one search, then discarded.",
    source: DIAGRAM,
  },
  "multi-photographer-event-gallery-diagram": {
    kind: "diagram",
    alt: "Diagram: three photographers at the start, on the course and at the finish each upload to their own collection in one event gallery, which participants search once by bib or selfie.",
    caption: "Several photographers, one gallery: each uploads to a collection, and participants search them all at once.",
    source: DIAGRAM,
  },
  "sponsor-free-download-photos-diagram": {
    kind: "diagram",
    alt: "Diagram: a sponsor pays an agreed fee, the photos are uploaded to a gallery in Sharing mode, the link is shared with thanks to the sponsor, and everyone downloads their originals free.",
    caption: "Sponsor-paid free photos: participants download free, and the sponsor travels with every shared link.",
    source: DIAGRAM,
  },
  "race-results-to-photos-diagram": {
    kind: "diagram",
    alt: "Diagram: a race results table with a Photos button on each row, leading to the event gallery searched by bib number 1423.",
    caption: "A photos link beside the results takes each participant to the gallery, where they search by bib.",
    source: DIAGRAM,
  },
  "organiser-photo-email-timeline-diagram": {
    kind: "diagram",
    alt: "Diagram: a timeline of five messages about photos: at registration, in race week, on race day when the gallery is live, in the results email, and a reminder a week later.",
    caption: "When to tell participants about their photos, from registration to a reminder a week later.",
    source: DIAGRAM,
  },
  "face-search-gdpr-checklist-diagram": {
    kind: "diagram",
    alt: "Checklist for organisers using face search: say it before the event, link the face data policy, offer bib search and browsing, make opting out easy, know the time limits, and think about children.",
    caption: "An organiser's checklist for face search at events. Not legal advice.",
    source: DIAGRAM,
  },
  "bib-selfie-or-time-search-diagram": {
    kind: "diagram",
    alt: "Diagram comparing three ways to find event photos: bib search when the number is readable, selfie search when the face is visible, and browsing by place and time when neither is.",
    caption: "Bib, selfie or time and place: each finds photos the others miss.",
    source: DIAGRAM,
  },
  "course-photographer-positions-diagram": {
    kind: "diagram",
    alt: "Diagram of a course with five photographer positions: the start, an early climb, a scenic point, the final stretch and the finish, each with the kind of photo it gives.",
    caption: "Photographer positions on a course. Each position can be its own collection in the gallery.",
    source: DIAGRAM,
  },
  "choosing-event-photography-partner-diagram": {
    kind: "diagram",
    alt: "Checklist of six questions for choosing an event photography partner: how fast photos are online, how people find theirs, free or paid, how face data is handled, whose brand people see, and who can use the photos.",
    caption: "Six questions to ask an event photography partner before you sign.",
    source: DIAGRAM,
  },
  "gallery-collections-by-class-diagram": {
    kind: "diagram",
    alt: "Diagram: one event gallery split into collections such as Ring 1 and Ring 2 with their classes, a wave with start, course and finish, and a stage with its checkpoints.",
    caption: "Split a gallery the way people remember the day: by ring and class, wave, stage or checkpoint.",
    source: DIAGRAM,
  },
};

const meta = MANIFEST as Record<string, { width: number; height: number; widths: number[] }>;

const build = (name: string, m: MediaMeta): MediaImage => {
  const files = meta[name];
  if (!files) throw new Error(`content image ${name}: no files (run scripts/content-images/encode.py)`);
  const set = (ext: "avif" | "webp" | "jpg") => files.widths.map((w) => `${mediaUrl(name, w, ext)} ${w}w`).join(", ");
  const mid = files.widths.find((w) => w >= 1200) ?? files.widths[files.widths.length - 1];
  return {
    ...m,
    name,
    width: files.width,
    height: files.height,
    widths: files.widths,
    src: mediaUrl(name, mid, "jpg"),
    srcSet: { avif: set("avif"), webp: set("webp"), jpg: set("jpg") },
  };
};

export const MEDIA: Record<string, MediaImage> = Object.fromEntries(Object.entries(MEDIA_META).map(([n, m]) => [n, build(n, m)]));

export const getMedia = (name: string | undefined): MediaImage | undefined => (name ? MEDIA[name] : undefined);

/** The names images.json has files for (media.test.ts checks every one is described here). */
export const MEDIA_FILES = Object.keys(meta);
